import React, { ButtonHTMLAttributes } from 'react';

import styled from 'styled-components';

const LOADER_POINT_SIZE = 8;
const LOADER_POINT_GAP = 16;

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  $width?: number;
  $isFillWidth?: boolean;
  $maxWidth?: number;
  $isFillHeight?: boolean;
  $height?: number;
  $fontSize?: number;
  $padding?: string;
  $borderRadius?: number;
  $children?: React.ReactNode;
  $isLoading?: boolean;
  $isReverse?: boolean;
}

const SButton = styled.button<ButtonProps>`
  width: ${({ $width, $maxWidth, $isFillWidth }) =>
    $isFillWidth || $maxWidth ? '100%' : $width ? `${$width}px` : 'initial'};
  max-width: ${({ $maxWidth }) => ($maxWidth ? `${$maxWidth}px` : 'initial')};
  height: ${({ $height, $isFillHeight }) => ($isFillHeight ? '100%' : $height ? `${$height}px` : '44px')};
  padding: ${({ $padding }) => $padding ?? '8px 24px'};
  font-size: ${({ $fontSize }) => ($fontSize ? `${$fontSize}px` : 'inherit')};
  border-radius: ${({ $borderRadius }) => `${$borderRadius ?? 6}px`};
  display: flex;
  align-items: center;
  justify-content: center;

  ${(props) => {
    if (props.$isReverse) {
      return {
        backgroundColor: props.theme.global.bgColor,
        color: props.theme.global.mainColor,
        border: `1px solid ${props.theme.global.mainColor}`,
      };
    } else {
      return {
        backgroundColor: props.theme.global.mainColor,
        color: props.theme.global.whiteTextColor,
      };
    }
  }}

  &:disabled {
    background-color: ${(props) => props.theme.ui.button.disabledBgColor};
    cursor: default;
  }
`;

const NewLoader = styled.div`
  position: relative;
  width: ${`${LOADER_POINT_GAP * 2 + LOADER_POINT_SIZE}px`};
  height: 100%;
  display: flex;
  align-items: center;

  & div {
    position: absolute;
    width: ${`${LOADER_POINT_SIZE}px`};
    height: ${`${LOADER_POINT_SIZE}px`};
    border-radius: 50%;
    background: #fff;
    animation-timing-function: cubic-bezier(0, 1, 1, 0);
  }

  div:nth-child(1) {
    left: 0px;
    animation: lds-ellipsis1 0.6s infinite;
  }
  div:nth-child(2) {
    left: 0px;
    animation: lds-ellipsis2 0.6s infinite;
  }
  div:nth-child(3) {
    left: ${`${LOADER_POINT_GAP}px`};
    animation: lds-ellipsis2 0.6s infinite;
  }
  div:nth-child(4) {
    left: ${`${LOADER_POINT_GAP * 2}px`};
    animation: lds-ellipsis3 0.6s infinite;
  }

  @keyframes lds-ellipsis1 {
    0% {
      transform: scale(0);
    }
    100% {
      transform: scale(1);
    }
  }
  @keyframes lds-ellipsis3 {
    0% {
      transform: scale(1);
    }
    100% {
      transform: scale(0);
    }
  }
  @keyframes lds-ellipsis2 {
    0% {
      transform: translate(0, 0);
    }
    100% {
      transform: ${`translate(${LOADER_POINT_GAP}px, 0)`};
    }
  }
`;

const Button = React.forwardRef(
  ({ children, $isLoading, disabled, ...rest }: ButtonProps, ref: React.ForwardedRef<HTMLButtonElement>) => {
    return (
      <SButton ref={ref} $isLoading={$isLoading} disabled={disabled || $isLoading} {...rest}>
        {$isLoading ? (
          <NewLoader>
            <div></div>
            <div></div>
            <div></div>
            <div></div>
          </NewLoader>
        ) : (
          children
        )}
      </SButton>
    );
  }
);

export default Button;
